import { Component, ElementRef, HostListener, computed, inject, output, signal } from '@angular/core';
import { toWatDateIso } from '#models';

interface DayCell {
  iso: string;
  day: number;
  blank: boolean;
  inRange: boolean;
  start: boolean;
  end: boolean;
  today: boolean;
}

export interface DateRange {
  from: string | null;
  to: string | null;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function iso(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function fromIso(s: string): Date {
  const [y, m, dd] = s.split('-').map(Number);
  return new Date(y, m - 1, dd);
}
function fmt(s: string): string {
  const d = fromIso(s);
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

@Component({
  selector: 'app-agent-runs-date-range',
  imports: [],
  templateUrl: './agent-runs-date-range.component.html',
  styleUrl: './agent-runs-date-range.component.scss',
})
export class AgentRunsDateRangeComponent {
  private _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  rangeChange = output<DateRange>();

  readonly dowLabels = DOW;

  // Calendar days follow WAT so "today" and presets match the WAT timestamps on the page.
  private _today = fromIso(toWatDateIso(new Date()));

  private _open = signal<boolean>(false);
  private _from = signal<string | null>(null);
  private _to = signal<string | null>(null);
  private _viewYear = signal<number>(this._today.getFullYear());
  private _viewMonth = signal<number>(this._today.getMonth());

  open = this._open.asReadonly();

  title = computed(() => `${MONTHS[this._viewMonth()]} ${this._viewYear()}`);

  triggerLabel = computed<{ text: string; all: boolean }>(() => {
    const from = this._from();
    const to = this._to();
    if (!from && !to) {
      return { text: 'All dates', all: true };
    }
    if (from && to) {
      if (from === to) {
        return { text: fmt(from), all: false };
      }
      const a = fromIso(from);
      const z = fromIso(to);
      if (a.getFullYear() === z.getFullYear()) {
        return {
          text: `${MONTHS_SHORT[a.getMonth()]} ${a.getDate()} – ${MONTHS_SHORT[z.getMonth()]} ${z.getDate()}, ${z.getFullYear()}`,
          all: false,
        };
      }
      return { text: `${fmt(from)} – ${fmt(to)}`, all: false };
    }
    return { text: `${fmt(from!)} – …`, all: false };
  });

  rangeLabel = computed(() => {
    const from = this._from();
    const to = this._to();
    if (!from && !to) {
      return 'No range selected';
    }
    if (from && to) {
      return `${fmt(from)} → ${fmt(to)}`;
    }
    return `${fmt(from!)} → pick end`;
  });

  calendar = computed<DayCell[]>(() => {
    const y = this._viewYear();
    const m = this._viewMonth();
    const from = this._from();
    const to = this._to();
    const todayIso = iso(this._today);
    const startDow = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();

    const cells: DayCell[] = [];
    for (let i = 0; i < startDow; i++) {
      cells.push({ iso: '', day: 0, blank: true, inRange: false, start: false, end: false, today: false });
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const ds = iso(new Date(y, m, day));
      let inRange = false;
      let start = false;
      let end = false;
      if (from && to) {
        inRange = ds > from && ds < to;
        start = ds === from;
        end = ds === to;
      } else if (from && ds === from) {
        start = true;
        end = true;
      }
      cells.push({ iso: ds, day, blank: false, inRange, start, end, today: ds === todayIso });
    }
    return cells;
  });

  toggle(event: MouseEvent): void {
    event.stopPropagation();
    if (this._open()) {
      this.close();
    } else {
      this._openPanel();
    }
  }

  prevMonth(): void {
    const prev = new Date(this._viewYear(), this._viewMonth() - 1, 1);
    this._viewYear.set(prev.getFullYear());
    this._viewMonth.set(prev.getMonth());
  }

  nextMonth(): void {
    const next = new Date(this._viewYear(), this._viewMonth() + 1, 1);
    this._viewYear.set(next.getFullYear());
    this._viewMonth.set(next.getMonth());
  }

  pick(cell: DayCell): void {
    if (cell.blank) {
      return;
    }
    const ds = cell.iso;
    const from = this._from();
    const to = this._to();
    if (!from || (from && to)) {
      this._from.set(ds);
      this._to.set(null);
    } else if (ds < from) {
      this._to.set(from);
      this._from.set(ds);
      this._emit();
    } else {
      this._to.set(ds);
      this._emit();
    }
  }

  applyPreset(preset: 'all' | '7' | '30' | 'month'): void {
    const today = this._today;
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (preset === 'all') {
      this._from.set(null);
      this._to.set(null);
    } else if (preset === 'month') {
      this._from.set(iso(new Date(today.getFullYear(), today.getMonth(), 1)));
      this._to.set(iso(todayStart));
      this._anchorView(new Date(today.getFullYear(), today.getMonth(), 1));
    } else {
      const n = Number(preset);
      const f = new Date(todayStart);
      f.setDate(f.getDate() - (n - 1));
      this._from.set(iso(f));
      this._to.set(iso(todayStart));
      this._anchorView(f);
    }
    this._emit();
  }

  clear(): void {
    this._from.set(null);
    this._to.set(null);
    this._emit();
  }

  done(): void {
    this._emit();
    this.close();
  }

  close(): void {
    this._open.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this._open() && !this._elementRef.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  private _openPanel(): void {
    this._open.set(true);
    const anchor = this._from() ? fromIso(this._from()!) : this._today;
    this._anchorView(anchor);
  }

  private _anchorView(anchor: Date): void {
    this._viewYear.set(anchor.getFullYear());
    this._viewMonth.set(anchor.getMonth());
  }

  private _emit(): void {
    this.rangeChange.emit({ from: this._from(), to: this._to() });
  }
}
